import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { type PathArray } from "common/utils/formStructure";
import { isDevelopment } from "common/utils/tools";

export type FieldPathContextValue = {
  /**
   * The current path as an array of segments
   */
  pathArray: PathArray;
};

const FieldPathContext = createContext<FieldPathContextValue | undefined>(
  undefined,
);

type RootPathProps = {
  path?: never;
  root: true;
};

type ChildPathProps = {
  path: string;
  root?: false;
};

export type FieldPathProviderProps = PropsWithChildren<
  RootPathProps | ChildPathProps
>;

export function FieldPathProvider({
  children,
  path,
  root,
}: FieldPathProviderProps) {
  const parent = useContext(FieldPathContext);
  const hasParent = !!parent;
  const pathArray = useMemo(() => {
    return root || !hasParent ? [] : parent.pathArray.concat(path);
  }, [path, root, hasParent, parent?.pathArray]);

  useEffect(() => {
    if (isDevelopment()) {
      if (!root && !hasParent) {
        throw new Error(
          "No parent was provided to FieldPathProvider but `root` is false.",
        );
      }
    }
  }, [hasParent, root]);

  const value: FieldPathContextValue = useMemo(
    () => ({
      pathArray,
    }),
    [pathArray],
  );

  return (
    <FieldPathContext.Provider value={value}>
      {children}
    </FieldPathContext.Provider>
  );
}

export function useFieldPath() {
  const context = useContext(FieldPathContext);

  if (!context) {
    throw new Error("useFieldPath must be used within a FieldPathProvider.");
  }

  return context;
}
